import { useEffect, useRef, useState } from "react";
import {
  loadResx,
  createTranslator,
} from "../../shared/helpers/resourceLoader";
import { useLocalization } from "../../context/LocalizationContext";
import { UserRole } from "../../shared/Enums/UserRole";
import { getCurrencySymbol } from "../../utils/currencySymbols";
import { useNavigate, useLocation } from "react-router-dom";
import apiManager from "../../api/apiManager";
import API_ENDPOINTS from "../../api/apiEndpoints";
import ServiceOrderStatus from "../../shared/Enums/ServiceOrderStatus";
import RepairOrderStatuses from "../RepairOrder/RepairOrderStatuses";
import ServiceAction from "../../shared/Enums/ServiceAction";
import { useSelectedDealership } from "../../hooks/useLocalStorage";
import getUserRoleFromJWT from "../../shared/helpers/getUserRoleFromJWT";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../shared/Services/toasterService";
import {
  showLoader,
  hideLoader,
} from "../../shared/Services/Loader/loaderService";
import LoadingOverlay from "../common/LoadingOverlay";
import { formatPhoneNumber } from "../../shared/helpers/formUtils";
import { useVehicleContext } from "../../context/VehicleContext";
import Swal from "sweetalert2";
import InfoCards from "./common/InfoCards";
import PriceSummary from "./common/PriceSummary";
import useServiceFetching from "../../hooks/useServiceFetching";
import useServiceTotals from "../../hooks/useServiceTotals";
import { normalizeCurrencyPrice } from "../../utils/price";
import ServiceListDisplay from "./common/ServiceListDisplay";
import ServiceTableRow from "./common/ServiceTableRow";
import CustomerApprovalModal from "./modals/CustomerApprovalModal";
import SentConfirmationModal from "./modals/SentConfirmationModal";

const InProgressPage = () => {
  const ctx = useVehicleContext();
  const {
    customerId,
    vehicle: propVehicle,
    status,
    setStatus,
    selectedServices,
    setSelectedServices,
    repairOrder,
    setRepairOrder,
  } = ctx;
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [showSentConfirmationModal, setShowSentConfirmationModal] =
    useState(false);
  const [originalSelectedServices, setOriginalSelectedServices] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState("");
  const { t } = useLocalization();
  const location = useLocation();
  const [customer, setCustomer] = useState(null);
  const [vehicle, setVehicle] = useState(
    propVehicle ?? location?.state?.vehicle ?? null,
  );
  const [dealership, setDealership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("oem");
  const id = customerId ?? location?.state?.customerId ?? null;
  const vehicleId = vehicle?.id;
  const repairOrderFromInProgress =
    location?.state?.repairOrderFromInProgress ?? null;
  const [currencyCode, setCurrencyCode] = useState("USD");
  const [isEditing, setIsEditing] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [pendingComplete, setPendingComplete] = useState(false);
  const [selectedDealershipId] = useSelectedDealership();
  const dealershipId = selectedDealershipId;
  const navigate = useNavigate();
  const [isApproved, setIsApproved] = useState(false);
  const { services, isLoading: servicesLoading } = useServiceFetching(
    vehicleId,
    dealershipId,
    t,
  );

  useEffect(() => {
    if (propVehicle && Object.keys(propVehicle).length > 0) {
      if (!vehicle || propVehicle.id !== vehicle.id) {
        setVehicle(propVehicle);
      }
    }
  }, [propVehicle]);

  useEffect(() => {
    if (
      status === ServiceOrderStatus.IN_PROGRESS &&
      originalSelectedServices.length === 0 &&
      selectedServices.length > 0
    ) {
      setOriginalSelectedServices(selectedServices.map((s) => ({ ...s })));
    }
  }, [status, selectedServices, originalSelectedServices.length]);

  useEffect(() => {
    const loadRepairOrderItems = async () => {
      const idToFetch = repairOrder?.[0]?.repairOrderId;
      if (!idToFetch) return;
      try {
        showLoader();
        const payload = { repairOrderId: idToFetch };
        const repairOrderData = await apiManager.post(
          API_ENDPOINTS.REPAIR_ORDER_ITEM,
          payload,
        );
        if (repairOrderData && repairOrderData.data.length > 0) {
          const backendServices = repairOrderData.data.map((item) => ({
            id: item.serviceId || `service-${Date.now()}`,
            serviceId: item.serviceId,
            serviceName: item.serviceName,
            description: item.serviceDescription,
            type: item.serviceCategory,
            price: normalizeCurrencyPrice(item.price ?? 0),
            status: item.status || 1,
            ...item,
          }));
          setSelectedServices(backendServices);
          setOriginalSelectedServices(backendServices.map((s) => ({ ...s })));
        } else {
          setSelectedServices([]);
          setOriginalSelectedServices([]);
        }
      } catch (err) {
        showErrorMessage("Failed to fetch repair order items");
        setSelectedServices([]);
        setOriginalSelectedServices([]);
      } finally {
        hideLoader();
      }
    };

    loadRepairOrderItems();
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await apiManager.post(
        API_ENDPOINTS.REPAIR_ORDER_GET_BY_ID,
        {
          repairOrderId: repairOrderFromInProgress || repairOrder.data,
        },
      );
      const data = response.data;
      // Only freeze button for terminal approval states, not for pending approvals
      if (data.statusCode === ServiceOrderStatus.APPROVAL_EXPIRED) {
        setIsApproved(true);
        setStatus(ServiceOrderStatus.APPROVAL_EXPIRED);
      } else if (data.statusCode === ServiceOrderStatus.DECLINED) {
        setIsApproved(true);
        setStatus(ServiceOrderStatus.DECLINED);
      } else {
        // For all other states (including pending approval), don't freeze on page load
        setIsApproved(false);
        if (data.statusCode) {
          setStatus(data.statusCode);
        }
      }
      if (data.items && data.items.length > 0) {
        const services = data.items.map((item) => ({
          ...item,
          id: item.serviceId || item.id,
          serviceName: item.serviceName,
          description: item.description,
          price: normalizeCurrencyPrice(item.price),
          type: item.serviceCategory || item.type,
          status: item.status || 1,
        }));
        setSelectedServices(services);
        setOriginalSelectedServices(services.map((s) => ({ ...s })));
      }
    } catch (error) {
      showErrorMessage(error);
    }
  };

  const { calculateServiceTypeTotal } = useServiceTotals(selectedServices);

  // Show all services in the table, but exclude declined (status === 3) from totals
  const allServices = selectedServices;
  const nonDeclinedServices = allServices.filter((s) => s.status !== 3);
  const subtotalAmount = nonDeclinedServices.reduce(
    (sum, service) => sum + (normalizeCurrencyPrice(service.price) || 0),
    0,
  );
  const taxRate = Number(dealership?.tax) || 0;
  const taxAmount = subtotalAmount * (taxRate / 100);
  const totalWithTax = subtotalAmount + taxAmount;

  const isServiceSelected = (service) => {
    return selectedServices.some(
      (s) =>
        (s.id && service.id && s.id === service.id) ||
        (s.serviceId && service.serviceId && s.serviceId === service.serviceId),
    );
  };

  const handleServiceSelect = (service, type) => {
    const isNewService = !isServiceSelected(service);
    if (isNewService) {
      const newService = {
        ...service,
        id: service.id || `service-${Date.now()}`,
        type: type,
        price: normalizeCurrencyPrice(service.price),
        status: 1,
      };
      setSelectedServices([...selectedServices, newService]);
    } else {
      const updatedServices = selectedServices.map((s) => {
        if (
          (s.id && service.id && s.id === service.id) ||
          (s.serviceId &&
            service.serviceId &&
            s.serviceId === service.serviceId)
        ) {
          return { ...s, type: s.status === 0 ? type : s.type, status: s.status === 0 ? 1 : 0 };
        }
        return s;
      });
      setSelectedServices(updatedServices);
    }
  };

  const handleRemoveService = (serviceId) => {
    Swal.fire({
      title: t("confirm_removal"),
      text: t("confirm_remove_service"),
      icon: "warning",
      showCancelButton: true,
      cancelButtonText: t("cancel"),
      confirmButtonText: t("delete"),
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      width: "280px",
      padding: "0.5em",
      iconColor: "#f39c12",
      customClass: {
        popup: "swal2-small-popup",
        title: "swal2-small-title",
        icon: "swal2-small-icon",
        confirmButton: "btn btn-danger btn-sm",
        cancelButton: "btn btn-secondary btn-sm",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        setSelectedServices(
          selectedServices.filter(
            (service) =>
              service.id !== serviceId && service.serviceId !== serviceId,
          ),
        );
      }
    });
  };

  const startEdit = () => {
    setIsEditing(true);
  };

  const handlePriceChange = (serviceId, newPrice) => {
    const updatedServices = selectedServices.map((s) => {
      if (s.id === serviceId || s.serviceId === serviceId) {
        return {
          ...s,
          price: normalizeCurrencyPrice(newPrice),
        };
      }
      return s;
    });
    setSelectedServices(updatedServices);
  };

  const cancelEdit = async () => {
    // Revert immediately to the last saved snapshot so unsaved selections are discarded
    setSelectedServices(originalSelectedServices.map((s) => ({ ...s })));
    try {
      const repairOrderId =
        repairOrderFromInProgress ||
        repairOrder?.data ||
        repairOrder?.[0]?.repairOrderId;
      if (!repairOrderId) return;

      // Then sync with the latest services saved on the server
      const response = await apiManager.post(
        API_ENDPOINTS.REPAIR_ORDER_GET_BY_ID,
        { repairOrderId },
      );

      const data = response.data;
      if (data.items && data.items.length > 0) {
        const latestServices = data.items.map((item) => ({
          ...item,
          id: item.serviceId || item.id,
          serviceName: item.serviceName,
          description: item.description,
          price: normalizeCurrencyPrice(item.price),
          type: item.serviceCategory || item.type,
          status: item.status || 1,
        }));

        setSelectedServices(latestServices);
        setOriginalSelectedServices(latestServices.map((s) => ({ ...s })));
      }
    } catch (error) {
      console.error("Error fetching latest services:", error);
    } finally {
      setIsEditing(false);
      // Keep button frozen if status is APPROVAL_EXPIRED, otherwise unfreeze
      if (status === ServiceOrderStatus.APPROVAL_EXPIRED) {
        setIsApproved(true);
      } else {
        setIsApproved(false);
      }
    }
  };

  const getRepairOrderId = () =>
    repairOrder?.data ||
    repairOrder?.repairOrderId ||
    repairOrderFromInProgress ||
    repairOrder?.[0]?.repairOrderId;

  // Only opens the approval popup; changes are saved when an approval method is confirmed in handleOk
  const handleUpdate = () => {
    setSelectedMethod("");
    setPendingComplete(true);
    setModalData({
      email: customer?.email,
      phoneNumber: formatPhoneNumber(customer?.phoneNumber),
      dealershipId: dealershipId,
      repairOrderId: getRepairOrderId(),
    });
    setShowApprovalModal(true);
  };

  const saveRepairOrder = async () => {
    let action;
    if (status === ServiceOrderStatus.NEW) {
      action = ServiceAction.ADDED;
    } else if (status === ServiceOrderStatus.IN_PROGRESS) {
      action = ServiceAction.UPDATED;
    } else {
      action = ServiceAction.UPDATED;
    }
    try {
      const payload = {
        repairOrderId: getRepairOrderId(),
        dealershipId: dealershipId,
        customerId: customer.id,
        customerVehicleId: vehicle.id,
        //odometer: vehicle.odometer,
        milesOk: true,
        dateOfService: new Date(),
        statusCode: status,
        action: action,
        items: selectedServices.map((s) => ({
          serviceCategory: s.type,
          serviceName: s.serviceName || s.displayName,
          serviceId: s.serviceId || s.id,
          serviceDescription: s.description,
          serviceBucket: s.serviceBucket,
          laborHours: s.laborHours,
          price: normalizeCurrencyPrice(s.price),
          isMileageBased: s.isMileageBased || false,
          minMileage: s.minMileage,
          maxMileage: s.maxMileage,
          source: s.source,
          status: s.status || 0, // Default to 0 if status is not set
        })),
      };
      const response = await apiManager.post(
        API_ENDPOINTS.REPAIR_ORDER_UPDATE,
        payload,
      );
      if (response.status == 200) {
        setRepairOrder(response.data);
        return true;
      }
      showErrorMessage(t("approval_failed"));
    } catch (err) {
      showErrorMessage(t("error_occurred"));
    }
    return false;
  };

  const handleOk = async () => {
    if (!modalData || !selectedMethod) return;

    // Save the edited services only after an approval method is confirmed
    showLoader();
    const saved = await saveRepairOrder();
    if (!saved) {
      hideLoader();
      return;
    }

    try {
      let success = false;
      if (selectedMethod === "email") {
        const emailPayload = {
          toEmail: modalData.email,
          subject: t("subject_service_update_approval"),
          dealershipId: modalData.dealershipId,
          repairOrderId: modalData.repairOrderId,
        };
        const res = await apiManager.post(
          API_ENDPOINTS.SEND_EMAIL,
          emailPayload,
          {
            headers: { "Content-Type": "application/json" },
          },
        );
        if (res.status === 200) {
          showSuccessMessage(t("success_email_sent"));
          setShowSentConfirmationModal(true);
          success = false;
        }
      }
      if (selectedMethod === "sms") {
        const smsPayload = {
          toNumber: formatPhoneNumber(modalData.phoneNumber),
          subject: t("subject_service_update_approval"),
          dealershipId: modalData.dealershipId,
          repairOrderId: modalData.repairOrderId,
        };
        const res = await apiManager.post(API_ENDPOINTS.SEND_SMS, smsPayload, {
          headers: { "Content-Type": "application/json" },
        });
        if (res.status === 200) {
          showSuccessMessage(t("success_sms_sent"));
          // Show confirmation modal after successful send instead of polling
          setShowSentConfirmationModal(true);
          success = false;
        }
      }
      if (selectedMethod === "self") {
        const statusPayload = {
          repairOrderId: modalData.repairOrderId,
          statusCode: status,
        };
        const response = await apiManager.post(
          API_ENDPOINTS.DEALER_ORDER_STATUS,
          statusPayload,
        );
        if (response.status == 200) {
          showSuccessMessage(t("success_service_updated"));
          setStatus(ServiceOrderStatus.IN_PROGRESS);
          setIsEditing(false);
          setIsApproved(false);
        }
      }
    } catch (err) {
      showErrorMessage(err);
    } finally {
      setShowApprovalModal(false);
      fetchData();
      hideLoader();
    }
  };

  const handleApprovalCancel = () => setShowApprovalModal(false);

  const handleMoveToComplete = async () => {
    setShowCompleteConfirm(true);
  };

  const confirmMoveToComplete = async () => {
    setShowCompleteConfirm(false);
    const repairOrderId = repairOrder[0]?.repairOrderId;
    const statusPayload = {
      repairOrderId: repairOrder?.data || repairOrderId,
      statusCode: ServiceOrderStatus.COMPLETED,
    };
    const response = await apiManager.post(
      API_ENDPOINTS.DEALER_ORDER_STATUS,
      statusPayload,
    );
    if (response.status == 200) {
      showSuccessMessage(t("service_completed_successfully"));
      if (location.state.fromRedirect) {
        navigate(location.pathname, {
          state: {
            ...location.state,
            status: ServiceOrderStatus.COMPLETED,
            goToStep6: true,
            goToStep5: false,
          },
          replace: true,
        });
      }
      setStatus(ServiceOrderStatus.COMPLETED);
      setShowApprovalModal(false);
      setPendingComplete(false);
      if (ctx && ctx.stepper && ctx.stepper.current) {
        ctx.stepper.current.to(6);
      }
    } else {
      showErrorMessage(t("failed_to_update_order_status"));
    }
  };

  useEffect(() => {
    const fetchDealership = async () => {
      try {
        showLoader();
        const response = await apiManager.post(API_ENDPOINTS.DEALER_GET_ID, {
          id: dealershipId,
        });
        if (response.status == 200) {
          const data = response.data;
          setDealership(data);
          setCurrencyCode(data.currencyCode || "USD");
        } else {
          showErrorMessage(t("error_fetch_dealership"));
        }
      } catch (err) {
        showErrorMessage(err);
        setDealership(null);
        setCurrencyCode("USD");
      } finally {
        hideLoader();
      }
    };

    const fetchDataAndServices = async () => {
      setLoading(true);
      setError(null);
      try {
        // fetch dealership
        await fetchDealership();

        // fetch customer + vehicle
        try {
          showLoader();
          const customerResponse = await apiManager.post(
            API_ENDPOINTS.CUSTOMER_GET_BY_ID,
            { id },
          );
          if (customerResponse.status == 200) {
            const data = customerResponse.data;
            setCustomer(data);

            const vid = vehicle?.id || null;
            const vehicleResponse = await apiManager.post(
              API_ENDPOINTS.CUSTOMER_GET_VEHICLE_BY_ID,
              { id: vid },
            );
            if (vehicleResponse.status == 200) {
              const vehicleData = vehicleResponse.data;
              setVehicle(vehicleData);
            } else {
              showErrorMessage(t("error_fetch_vehicle"));
            }
          } else {
            showErrorMessage(t("error_fetch_customer"));
          }
        } catch (err) {
          showErrorMessage(err);
        } finally {
          hideLoader();
        }
        // Services are now fetched via useServiceFetching hook
      } finally {
        setLoading(false);
      }
    };

    fetchDataAndServices();
  }, [id]);

  const haveServicesChanged = () => {
    if (selectedServices.length === 0) return false;

    if (selectedServices.length !== originalSelectedServices.length)
      return true;

    const sortFn = (s) =>
      (s.id || s.serviceId || s.serviceName || "") + (s.price || "");
    const a = [...selectedServices].map(sortFn).sort();
    const b = [...originalSelectedServices].map(sortFn).sort();

    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return true;
    }

    return false;
  };

  const statusDisplayMap = {
    [ServiceOrderStatus.NEW]: RepairOrderStatuses.NEW,
    [ServiceOrderStatus.SIGNED]: RepairOrderStatuses.SIGNED,
    [ServiceOrderStatus.IN_PROGRESS]: RepairOrderStatuses.IN_PROGRESS,
    [ServiceOrderStatus.COMPLETED]: RepairOrderStatuses.COMPLETED,
    [ServiceOrderStatus.PENDING_APPROVAL]: RepairOrderStatuses.PENDING_APPROVAL,
    [ServiceOrderStatus.APPROVAL_EXPIRED]: RepairOrderStatuses.APPROVAL_EXPIRED,
    [ServiceOrderStatus.DECLINED]: RepairOrderStatuses.DECLINED,
  };

  const getStatusDisplay = (statusCode) => {
    const normalizedStatus = statusCode;
    return statusDisplayMap[normalizedStatus] || statusCode;
  };

  const isLoading = loading || servicesLoading;
  const serviceError = error || services[activeTab]?.error;

  if (serviceError)
    return <div className="alert alert-danger">{serviceError}</div>;

  return (
    <div>
      {isLoading && <LoadingOverlay />}

      <div className="container-fluid p-0 mb-2">
        <div className="row">
          <div className="col-md-6">
            <div style={{ fontWeight: "bold", fontSize: 18 }}>
              {t("status")}:{" "}
              <span style={{ color: "#ff9800" }}>
                {getStatusDisplay(status)}
              </span>
            </div>
          </div>
          <div className="col-md-6">
            <div className="d-flex justify-content-end gap-2">
              <button
                className="btn btn-success"
                onClick={handleMoveToComplete}
                disabled={isEditing || isApproved}
              >
                {t("move_to_complete")}
              </button>
              <button
                className="btn btn-blue btn-sm"
                onClick={() => navigate("/second-step")}
              >
                {t("back")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Complete Confirmation Modal */}
      {showCompleteConfirm && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body">{t("confirm_complete_service")}</div>
              <div className="modal-footer">
                <button
                  className="btn btn-success"
                  onClick={confirmMoveToComplete}
                >
                  {t("yes")}
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => setShowCompleteConfirm(false)}
                >
                  {t("no")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="select-services-content">
        <div className="container-fluid p-0">
          <InfoCards t={t} vehicle={vehicle} customer={customer} />
          <div className="col-md-12">
            <div className="card mb-4">
              <PriceSummary
                t={t}
                currencyCode={currencyCode}
                calculateServiceTypeTotal={calculateServiceTypeTotal}
              />
              <div className="card-body">
                <div className="container-fluid">
                  <div className="row">
                    <div className="col-lg-6 tab-content-card">
                      <ServiceListDisplay
                        services={services}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        isServiceSelected={isServiceSelected}
                        onServiceSelect={handleServiceSelect}
                        currencyCode={currencyCode}
                        selectDisabled={!isEditing}
                        t={t}
                      />
                    </div>
                    <div className="col-lg-6 selected-card">
                      <h2>{t("selected")}</h2>
                      <table className="table">
                        <tbody>
                          {allServices.length > 0 ? (
                            allServices.map((service, index) => (
                              <ServiceTableRow
                                key={`selected-${service.id || index}`}
                                service={service}
                                index={index}
                                currencyCode={currencyCode}
                                onRemove={() =>
                                  handleRemoveService(
                                    service.id || service.serviceId,
                                  )
                                }
                                canRemove={isEditing}
                                onPriceChange={handlePriceChange}
                                canEdit={isEditing}
                                isDeclined={service.status === 3}
                                t={t}
                              />
                            ))
                          ) : (
                            <tr className="no-services-text">
                              <td colSpan="4" className="text-center py-3">
                                {t("no_services_selected")}
                              </td>
                            </tr>
                          )}
                          {nonDeclinedServices.length > 0 && (
                            <tr
                              className="total-row"
                              style={{ borderTop: "2px solid #dee2e6" }}
                            >
                              <td></td>
                              <td className="text-start text-muted">
                                {t("tax")} ({taxRate}%)
                              </td>
                              <td className="mulish-extra-bold text-muted">
                                {getCurrencySymbol(currencyCode)}
                                {taxAmount.toFixed(2)}
                              </td>
                              <td></td>
                            </tr>
                          )}
                          {nonDeclinedServices.length > 0 && (
                            <tr
                              className="total-row"
                              style={{
                                borderTop: "1px solid #dee2e6",
                                fontSize: "1.05rem",
                              }}
                            >
                              <td></td>
                              <td className="text-start fw-bold">
                                {t("total")}
                              </td>
                              <td className="mulish-extra-bold">
                                {getCurrencySymbol(currencyCode)}
                                {totalWithTax.toFixed(2)}
                              </td>
                              <td></td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-footer bg-gray d-flex justify-content-center align-items-center gap-2">
                {(status === ServiceOrderStatus.IN_PROGRESS ||
                  status === ServiceOrderStatus.DECLINED ||
                  status === ServiceOrderStatus.APPROVAL_EXPIRED) &&
                  !isEditing && (
                    <>
                      <button className="btn btn-success" onClick={startEdit}>
                        {t("edit")}
                      </button>
                      <button
                        className="btn btn-cancel"
                        onClick={() => {
                          navigate("/repair-orders");
                        }}
                      >
                        {t("cancel")}
                      </button>
                    </>
                  )}
                {(status === ServiceOrderStatus.IN_PROGRESS ||
                  status === ServiceOrderStatus.DECLINED ||
                  status === ServiceOrderStatus.APPROVAL_EXPIRED) &&
                  isEditing && (
                    <>
                      <button
                        className="btn btn-success"
                        onClick={handleUpdate}
                        disabled={!haveServicesChanged()}
                      >
                        {t("update")}
                      </button>
                      <button className="btn btn-cancel" onClick={cancelEdit}>
                        {t("cancel")}
                      </button>
                    </>
                  )}
              </div>
              {/* Approval Modal */}
              <CustomerApprovalModal
                show={showApprovalModal}
                modalData={modalData}
                showSmsOption={Boolean(customer?.smsConsent)}
                selectedMethod={selectedMethod}
                setSelectedMethod={setSelectedMethod}
                onOk={handleOk}
                onCancel={handleApprovalCancel}
                t={t}
              />
              {/* Sent Confirmation Modal */}
              <SentConfirmationModal
                show={showSentConfirmationModal}
                selectedMethod={selectedMethod}
                onGoToDashboard={() => {
                  setShowSentConfirmationModal(false);
                  const userRole = getUserRoleFromJWT();
                  navigate(
                    userRole === UserRole.DEALER_ADMIN
                      ? "/admin-dashboard"
                      : "/service-desk-dashboard",
                  );
                }}
                t={t}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InProgressPage;
